import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Users, Plus, Loader2 } from 'lucide-react';

import CodeCardsPrintView from './CodeCardsPrintView';
import { StudentCard } from '@/components/admin/students/StudentCard';
import { StudentFormDialog } from '@/components/admin/students/StudentFormDialog';
import { StudentToolbar } from '@/components/admin/students/StudentToolbar';
import { CsvImportDialog } from '@/components/admin/students/CsvImportDialog';

import { API } from '@/lib/apiBase';

export default function AdminStudents() {
  const { getAuthHeaders } = useAuth();
  const { t } = useLanguage();
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [selectedClass, setSelectedClass] = useState('all');
  const [copiedCode, setCopiedCode] = useState(null);
  const [formData, setFormData] = useState({ name: '', class_id: '' });
  const [csvClassId, setCsvClassId] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [loadingPrint, setLoadingPrint] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const getFirstClassId = () => (classes.length > 0 ? classes[0].class_id : '');

  const siteUrl = window.location.origin;

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [studentsRes, classesRes] = await Promise.all([
        fetch(`${API}/students${selectedClass !== 'all' ? `?class_id=${selectedClass}` : ''}`, {
          headers, credentials: 'include',
        }),
        fetch(`${API}/classes`, { headers, credentials: 'include' }),
      ]);
      if (studentsRes.ok) setStudents(await studentsRes.json());
      if (classesRes.ok) setClasses(await classesRes.json());
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [selectedClass]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingStudent ? `${API}/students/${editingStudent.student_id}` : `${API}/students`;
      const response = await fetch(url, {
        method: editingStudent ? 'PUT' : 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      if (!editingStudent && data.unique_code) {
        toast.success(`${t('students.studentCreated')}: ${data.unique_code}`);
      } else {
        toast.success(editingStudent ? t('students.studentUpdated') : t('students.studentCreated'));
      }
      setDialogOpen(false);
      setEditingStudent(null);
      setFormData({ name: '', class_id: '' });
      fetchData();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleDelete = async (studentId) => {
    if (!window.confirm(t('students.confirmDelete'))) return;
    try {
      const response = await fetch(`${API}/students/${studentId}`, {
        method: 'DELETE', headers: getAuthHeaders(), credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed');
      toast.success(t('students.studentDeleted'));
      fetchData();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleCsvImport = async () => {
    if (!csvFile || !csvClassId) {
      toast.error(t('students.importFailed'));
      return;
    }
    setImporting(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', csvFile);
      formDataObj.append('class_id', csvClassId);
      const response = await fetch(`${API}/admin/students/import-csv`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: formDataObj,
      });
      if (!response.ok) throw new Error('Import failed');
      const result = await response.json();
      toast.success(`${t('students.importSuccess')}: ${result.imported}`);
      setCsvDialogOpen(false);
      setCsvFile(null);
      setCsvClassId('');
      fetchData();
    } catch (error) {
      toast.error(t('students.importFailed'));
    } finally {
      setImporting(false);
    }
  };

  const handlePrintCodes = async () => {
    if (classes.length === 0) {
      toast.error(t('students.noClassSelected'));
      return;
    }
    setLoadingPrint(true);
    try {
      // When "all" is selected, fetch every class and include them all in one print job
      const classIds = selectedClass !== 'all'
        ? [selectedClass]
        : classes.map((c) => c.class_id);

      const results = await Promise.all(classIds.map((cid) => (
        fetch(`${API}/admin/classes/${cid}/code-cards`, {
          headers: getAuthHeaders(), credentials: 'include',
        }).then((r) => (r.ok ? r.json() : null))
      )));

      const valid = results.filter((d) => d && d.students && d.students.length > 0);

      if (valid.length === 0) {
        toast.error(t('students.noStudents'));
        return;
      }

      // The print view accepts `classes` (multi) OR `data` (single). Pass multi-class.
      setPrintData({ classes: valid });
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoadingPrint(false);
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      toast.success(t('students.codeCopied'));
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const openEditDialog = (student) => {
    setEditingStudent(student);
    setFormData({ name: student.name, class_id: student.class_id });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingStudent(null);
    setFormData({ name: '', class_id: getFirstClassId() });
    setDialogOpen(true);
  };

  const getClassName = (classId) => {
    const cls = classes.find((c) => c.class_id === classId);
    return cls ? `${cls.name} (${cls.year})` : classId;
  };

  const filteredStudents = useMemo(() => {
    if (!searchQuery.trim()) return students;
    const q = searchQuery.toLowerCase();
    return students.filter((student) => (
      student.name.toLowerCase().includes(q) ||
      student.unique_code.toLowerCase().includes(q) ||
      getClassName(student.class_id).toLowerCase().includes(q)
    ));
  }, [students, searchQuery, classes]); // eslint-disable-line react-hooks/exhaustive-deps

  if (printData) {
    return <CodeCardsPrintView data={printData} siteUrl={siteUrl} t={t} onClose={() => setPrintData(null)} />;
  }

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-12" data-testid="admin-students">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">{t('students.title')}</h1>
          <p className="text-muted-foreground">{t('students.description')}</p>
        </div>

        <StudentToolbar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          classes={classes}
          selectedClass={selectedClass}
          onSelectedClassChange={setSelectedClass}
          onOpenCsv={() => { setCsvDialogOpen(true); setCsvClassId(getFirstClassId()); }}
          onPrintCodes={handlePrintCodes}
          loadingPrint={loadingPrint}
          onOpenCreate={openCreateDialog}
        />
      </div>

      {classes.length === 0 && !loading && (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <Users className="w-10 h-10 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-bold mb-2">{t('students.createClassFirst')}</h3>
          <p className="text-muted-foreground">{t('students.createClassFirstDesc')}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : students.length === 0 && classes.length > 0 ? (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <Users className="w-10 h-10 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-bold mb-2">{t('students.noStudents')}</h3>
          <p className="text-muted-foreground mb-6">{t('students.noStudentsDesc')}</p>
          <Button onClick={openCreateDialog} className="bg-primary hover:bg-primary/90">
            <Plus className="w-4 h-4 mr-2" />
            {t('students.addStudent')}
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredStudents.map((student) => (
            <StudentCard
              key={student.student_id}
              student={student}
              className={getClassName(student.class_id)}
              copiedCode={copiedCode}
              onEdit={openEditDialog}
              onDelete={handleDelete}
              onCopyCode={copyCode}
            />
          ))}
        </div>
      )}

      <StudentFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editingStudent={editingStudent}
        formData={formData}
        setFormData={setFormData}
        classes={classes}
        onSubmit={handleSubmit}
      />

      <CsvImportDialog
        open={csvDialogOpen}
        onOpenChange={setCsvDialogOpen}
        classes={classes}
        csvClassId={csvClassId}
        setCsvClassId={setCsvClassId}
        csvFile={csvFile}
        setCsvFile={setCsvFile}
        importing={importing}
        onImport={handleCsvImport}
        onCancel={() => setCsvDialogOpen(false)}
      />
    </div>
  );
}
